import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { SxProps } from '@mui/material'

import SearchField from 'components_new/molecules/SearchField'

import { useTranslation } from 'language/translator'
import { debounce as _debounce } from 'lodash'

type Props = {
  debounce?: boolean | number
  fullWidth?: boolean
  sx?: SxProps
  search: string | null
  setSearch: (value: string | null) => void
}

const StandardSearch = React.memo((props: Props) => {
  const { debounce, fullWidth, sx, search, setSearch } = props

  const { t } = useTranslation()

  const [focused, setFocused] = useState<boolean>(false)
  const [input, setInput] = useState<string>(search ?? '')

  const debouncedSetQuery = useMemo(
    () =>
      _debounce((value: string | null) => {
        setSearch(value)
      }, 500),
    [setSearch]
  )

  const handleSearch = useCallback(
    (value: string) => {
      if (!debounce) {
        setSearch(value === '' ? null : value)
      } else {
        setInput(value)
        debouncedSetQuery(value === '' ? null : value)
      }
    },
    [setSearch, debouncedSetQuery]
  )

  const handleClear = useCallback(() => {
    if (!debounce) {
      setSearch(null)
    } else {
      setInput('')
      debouncedSetQuery(null)
    }
  }, [setSearch, debouncedSetQuery])

  useEffect(() => {
    if (!debounce) {
      setInput(search ?? '')
    }
  }, [search])

  // cleanup
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel()
    }
  }, [debouncedSetQuery])

  // success
  return (
    <SearchField
      onBlur={() => setFocused(false)}
      onClear={handleClear}
      onFocus={() => setFocused(true)}
      onSearch={handleSearch}
      placeholder={`${t('global', 'search')}...`}
      size="small"
      sx={{
        background: '#fff',
        transition: 'width 0.5s ease',
        width: fullWidth ? undefined : focused ? 296 : 192,
        ...sx
      }}
      value={input}
    />
  )
})

export default StandardSearch
